import React from 'react';
import SafeSEO from '@/components/SafeSEO';
import { motion } from 'framer-motion';
import { Leaf, Users, TrendingUp, Heart, Globe, Recycle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Sustainability = () => {
  const { t } = useLanguage();

  const esgPillars = [
    {
      icon: Leaf,
      title: t('sustainability.items.environmental.title'),
      description: t('sustainability.items.environmental.desc'),
    },
    {
      icon: Users,
      title: t('sustainability.items.social.title'),
      description: t('sustainability.items.social.desc'),
    },
    {
      icon: TrendingUp,
      title: t('sustainability.items.governance.title'),
      description: t('sustainability.items.governance.desc'),
    },
  ];

  const commitments = [
    {
      icon: Globe,
      title: t('sustainability.items.ods.title'),
      description: t('sustainability.items.ods.desc'),
    },
    {
      icon: Recycle,
      title: t('sustainability.items.circular.title'),
      description: t('sustainability.items.circular.desc'),
    },
    {
      icon: Heart,
      title: t('sustainability.items.positive.title'),
      description: t('sustainability.items.positive.desc'),
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('sustainability.title')}
        description={t('sustainability.subtitle')}
        url="https://consulogic-gt.com/sostenibilidad"
      />

      <div className="pt-20">
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('sustainability.title')}</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                {t('sustainability.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                  {t('sustainability.commitmentTitle')}
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {t('sustainability.commitmentText1')}
                </p>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {t('sustainability.commitmentText2')}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {t('sustainability.commitmentText3')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img 
                  className="w-full h-[500px] object-cover rounded-2xl shadow-2xl" 
                  alt="Sustainable energy and green technology"
                 src="https://images.unsplash.com/photo-1697869162556-ab57db502c09" />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-4">
                {t('sustainability.pillarsTitle')}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t('sustainability.pillarsSubtitle')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
              {esgPillars.map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-xl flex items-center justify-center mb-6">
                    <pillar.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#0A5F7F] mb-4">{pillar.title}</h3>
                  <p className="text-gray-600 mb-6">{pillar.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {commitments.map((commitment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-full flex items-center justify-center mx-auto mb-4">
                    <commitment.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0A5F7F] mb-3">{commitment.title}</h3>
                  <p className="text-gray-600">{commitment.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <img 
                    className="w-full h-[500px] object-cover rounded-2xl shadow-2xl" 
                    alt="Community development and social impact"
                   src="https://images.unsplash.com/photo-1513566896764-358e5c2ca5eb" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                    {t('sustainability.impactTitle')}
                  </h2>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {t('sustainability.impactText1')}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {t('sustainability.impactText2')}
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Sustainability;